import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { WarningCircle, Phone } from '@phosphor-icons/react';
import { apptStatusColors, pickTextColor } from './agendaConstants';

const statusLabels = {
  citada: 'Citada', anulada: 'Anulada', no_asiste: 'No asiste',
  sala_espera: 'Sala espera', especialista: 'Especialista',
  sondas: 'Sondas', terminada: 'Terminada',
};

function HoverInfo({ appt, study, anchorRect }) {
  const isQsalud = !appt.study_id || appt.visit_code === 'Qsalud';
  const sc = apptStatusColors[appt.status];
  const popRef = useRef(null);
  const [style, setStyle] = useState({ opacity: 0, position: 'fixed', pointerEvents: 'none', zIndex: 9999 });

  useEffect(() => {
    if (!popRef.current || !anchorRect) return;
    const pop = popRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Prefer showing above the chip; if not enough space, show below
    let top;
    if (anchorRect.top - pop.height - 6 > 0) {
      top = anchorRect.top - pop.height - 6;
    } else {
      top = anchorRect.bottom + 6;
    }

    // Horizontal: align to left edge of chip, shift if overflows right
    let left = anchorRect.left;
    if (left + pop.width > vw - 12) {
      left = vw - pop.width - 12;
    }
    if (left < 8) left = 8;

    // Clamp top
    if (top + pop.height > vh - 8) top = vh - pop.height - 8;
    if (top < 8) top = 8;

    setStyle({
      position: 'fixed',
      top,
      left,
      zIndex: 9999,
      pointerEvents: 'none',
      opacity: 1,
      transition: 'opacity 120ms ease-in',
    });
  }, [anchorRect]);

  const content = (
    <div
      ref={popRef}
      data-testid={`appt-hover-${appt.appointment_id}`}
      className="w-64 bg-white border border-stone-200 rounded-md shadow-xl overflow-hidden"
      style={style}
    >
      <div className="h-1.5" style={{ backgroundColor: study?.color || (isQsalud ? '#059669' : '#6B7280') }} />
      <div className="p-3 space-y-2.5">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: study?.color || (isQsalud ? '#059669' : '#6B7280') }} />
          <span className="text-xs font-semibold text-stone-800 truncate">
            {isQsalud ? `${appt.visit_code === 'QSALUD TFNO' ? 'QSALUD TFNO' : 'Qsalud'} — Sin estudio` : `${study?.code || ''} — ${study?.title || ''}`}
          </span>
        </div>

        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
          <span className="text-stone-400 font-medium">Voluntario</span>
          <span className="text-stone-800 font-semibold truncate flex items-center gap-1">
            {appt.volunteer_observations && (
              <WarningCircle
                weight="fill"
                size={14}
                color="#DC2626"
                className="shrink-0"
                style={{ filter: 'drop-shadow(0 0 1px #fff)' }}
              />
            )}
            <span className="truncate">
              {appt.study_volunteer_code ? `${appt.study_volunteer_code} — ` : ''}
              {appt.volunteer_name || '—'}
            </span>
          </span>

          <span className="text-stone-400 font-medium">Visita</span>
          <span className="text-stone-800 font-mono">{appt.visit_code || '—'}</span>

          <span className="text-stone-400 font-medium">Fecha</span>
          <span className="text-stone-800 font-mono">{appt.date}</span>

          <span className="text-stone-400 font-medium">Hora</span>
          <span className="text-stone-800 font-mono">{appt.time || '—'}</span>

          <span className="text-stone-400 font-medium">Estado</span>
          <span>
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${sc ? `${sc.bg} ${sc.text} ${sc.border}` : 'bg-stone-100 text-stone-600'}`}>
              {statusLabels[appt.status] || appt.status}
            </span>
          </span>
        </div>

        {appt.volunteer_observations && (
          <div className="border-t border-red-100 pt-2 mt-1 bg-red-50/50 -mx-3 px-3 py-2">
            <p className="text-[10px] text-red-700 font-semibold mb-0.5 flex items-center gap-1">
              <WarningCircle weight="fill" size={12} color="#DC2626" />
              Observaciones del voluntario
            </p>
            <p className="text-xs text-stone-700 leading-relaxed whitespace-pre-wrap break-words line-clamp-4">{appt.volunteer_observations}</p>
          </div>
        )}

        {appt.notes && (
          <div className="border-t border-stone-100 pt-2 mt-1">
            <p className="text-[10px] text-stone-400 font-medium mb-0.5">Notas</p>
            <p className="text-xs text-stone-600 leading-relaxed whitespace-pre-wrap break-words line-clamp-4">{appt.notes}</p>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export function ApptChip({ appt, study, onClick, compact, onDragStart, spanCells }) {
  const [hovered, setHovered] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const hoverTimer = useRef(null);
  const chipRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    hoverTimer.current = setTimeout(() => {
      if (chipRef.current) {
        setAnchorRect(chipRef.current.getBoundingClientRect());
      }
      setHovered(true);
    }, 250);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hoverTimer.current);
    setHovered(false);
    setAnchorRect(null);
  }, []);

  const color = study?.color || (!appt.study_id || appt.visit_code === 'Qsalud' ? '#059669' : '#6B7280');
  // URS-F339: el texto se adapta al color del estudio. Para fondos claros
  // (amarillos, lima, beige) usamos negro suave; para oscuros, blanco.
  const textColor = pickTextColor(color);
  const isLightBg = textColor === '#111';
  // URS-F305: spanCells>=2 (10min+) muestra 2 lineas: arriba hora+visita+estado,
  // abajo voluntario. Para 5min (1 cell) usamos una linea con hora + vol + estado.
  // Etiquetas de estado SIN abreviar (truncan si no caben).
  const tall = (spanCells || 1) >= 2;
  const status = appt.status || '';
  const statusFull = statusLabels[status] || status || '';
  const sc = apptStatusColors[status];
  // URS-F430: alerta amarilla+halo blanco si el voluntario tiene "Observaciones"
  // en Datos Personales. El halo blanco garantiza que destaque sobre cualquier
  // color de estudio (incluso amarillo o rojo).
  const phoneIcon = appt.visit_code === 'QSALUD TFNO'
    ? <Phone weight="bold" size={12} className="shrink-0" data-testid={`appt-phone-icon-${appt.appointment_id}`} />
    : null;
  const hasObs = !!(appt.volunteer_observations && String(appt.volunteer_observations).trim());
  const obsIcon = hasObs ? (
    <WarningCircle
      weight="fill"
      size={14}
      color="#DC2626"
      className="shrink-0"
      style={{ filter: 'drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff)' }}
      data-testid={`appt-obs-alert-${appt.appointment_id}`}
    />
  ) : null;
  // Badge con colores semanticos del estado para distinguirlo del color del estudio.
  // Si el badge no tiene clase definida, el fallback se adapta al fondo: en
  // fondos oscuros tinta blanca, en claros tinta negra para mantener contraste.
  const fallbackBadge = isLightBg
    ? 'bg-black/10 text-stone-800 border-black/20'
    : 'bg-white/30 text-white border-white/40';
  const statusBadgeCls = `inline-block px-1 py-[1px] rounded-sm text-[9px] font-bold leading-none border ${sc ? `${sc.bg} ${sc.text} ${sc.border}` : fallbackBadge}`;

  return (
    <div
      className="relative w-full h-full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={chipRef}
    >
      <button
        data-testid={`appt-chip-${appt.appointment_id}`}
        draggable
        onDragStart={(e) => { e.stopPropagation(); setHovered(false); setAnchorRect(null); onDragStart && onDragStart(e, appt); }}
        onClick={(e) => { e.stopPropagation(); onClick(appt); }}
        className={`w-full h-full text-left rounded px-1.5 py-0.5 text-xs font-semibold transition-all hover:brightness-110 hover:shadow-sm cursor-grab active:cursor-grabbing ${tall ? 'flex flex-col justify-start overflow-hidden gap-0.5' : 'flex items-center gap-1 overflow-hidden'}`}
        style={{ backgroundColor: color, color: textColor }}
      >
        {tall ? (
          <>
            <span className="flex items-center gap-1 text-[10px] leading-tight min-w-0">
              <span className="font-mono opacity-90 shrink-0">{appt.time || '--:--'}</span>
              {phoneIcon}
              {appt.visit_code && <span className="opacity-80 shrink-0">· {appt.visit_code}</span>}
              <span className={`${statusBadgeCls} truncate`} title={statusFull}>{statusFull}</span>
            </span>
            <span className="flex items-center gap-1 min-w-0 leading-tight">
              {obsIcon}
              <span className="truncate">{appt.volunteer_name ? `${appt.study_volunteer_code} — ${appt.volunteer_name}` : appt.study_volunteer_code}</span>
            </span>
          </>
        ) : (
          // 5 min (1 cell): una linea con hora + estado + vol
          <>
            {!compact && <span className="font-mono opacity-90 shrink-0">{appt.time || '--:--'}</span>}
            {phoneIcon}
            <span className={`${statusBadgeCls} truncate`} title={statusFull}>{statusFull}</span>
            {obsIcon}
            <span className="truncate flex-1 min-w-0">{appt.study_volunteer_code || (appt.volunteer_name || '')}</span>
          </>
        )}
      </button>
      {hovered && anchorRect && <HoverInfo appt={appt} study={study} anchorRect={anchorRect} />}
    </div>
  );
}
